import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../dialog/ConfirmDialog";
import {
  buildAdvancedPushRequest,
  buildPushPreviewRequest,
  createPushIdempotencyKey,
  validateAdvancedPushDraft,
} from "../../helpers/push-notifications";
import {
  useCreatePushNotification,
  usePushAudiencePreview,
  usePushImageActions,
  usePushUsers,
} from "../../hooks/push-notifications";
import AudiencePreview from "./AudiencePreview";
import PushFeedback from "./PushFeedback";

const initialDraft = {
  title: "",
  message: "",
  category: "PROMOTION",
  platforms: ["ANDROID", "IOS"],
  audience: { type: "ALL" },
  imageAsset: null,
  scheduledFor: null,
};

const buildAudience = (type, selectedUsers, segmentCode, segmentDays) => {
  if (type === "USERS") {
    return { type: "USERS", userIds: [...selectedUsers.keys()] };
  }
  if (type === "SEGMENT") {
    return {
      type: "SEGMENT",
      segment: {
        code: segmentCode,
        parameters: { days: Number(segmentDays) },
      },
    };
  }
  return { type: "ALL" };
};

function UserSelector({ selectedUsers, onChange }) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const usersQuery = usePushUsers(search, page, 20, true);
  const users = usersQuery.data?.data ?? [];
  const metadata = usersQuery.data?.metadata;

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const toggleUser = (user) => {
    const next = new Map(selectedUsers);
    if (next.has(user.id)) {
      next.delete(user.id);
    } else {
      next.set(user.id, user);
    }
    onChange(next);
  };

  return (
    <div className="rounded border border-slate-200 bg-white p-4">
      <label className="text-sm font-semibold">
        Search users
        <input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Name or email"
          className="mt-2 w-full rounded border border-slate-200 px-3 py-2 font-normal"
        />
      </label>
      {selectedUsers.size ? (
        <div className="mt-3 flex flex-wrap gap-2" aria-label="Selected users">
          {[...selectedUsers.values()].map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => toggleUser(user)}
              className="rounded-full bg-sky-100 px-3 py-1 text-xs text-sky-800"
              title="Remove user"
            >
              {user.userInfo?.email || user.username || `User ${user.id}`} ×
            </button>
          ))}
        </div>
      ) : null}
      <div className="mt-4 max-h-64 divide-y overflow-y-auto border-y border-slate-100">
        {usersQuery.isLoading ? (
          <p className="p-3 text-sm text-slate-500">Loading users…</p>
        ) : users.length ? (
          users.map((user) => {
            const name = [user.userInfo?.firstName, user.userInfo?.lastName]
              .filter(Boolean)
              .join(" ");
            return (
              <label
                key={user.id}
                className="flex cursor-pointer items-center gap-3 p-3 text-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedUsers.has(user.id)}
                  onChange={() => toggleUser(user)}
                />
                <span>
                  <span className="block font-medium">
                    {name || user.username}
                  </span>
                  <span className="text-xs text-slate-500">
                    {user.userInfo?.email || user.username}
                  </span>
                </span>
              </label>
            );
          })
        ) : (
          <p className="p-3 text-sm text-slate-500">No users found.</p>
        )}
      </div>
      {(metadata?.totalPages ?? 0) > 1 ? (
        <div className="mt-3 flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((value) => value - 1)}
            className="rounded border px-3 py-1 disabled:opacity-40"
          >
            Previous
          </button>
          <span>
            Page {page + 1} of {metadata.totalPages}
          </span>
          <button
            type="button"
            disabled={page >= metadata.totalPages - 1}
            onClick={() => setPage((value) => value + 1)}
            className="rounded border px-3 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AdvancedPushNotificationForm({ capabilities }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(initialDraft);
  const [audienceType, setAudienceType] = useState("ALL");
  const [selectedUsers, setSelectedUsers] = useState(new Map());
  const [segmentCode, setSegmentCode] = useState("RECENTLY_JOINED");
  const [segmentDays, setSegmentDays] = useState("30");
  const [debouncedRequest, setDebouncedRequest] = useState(null);
  const [errors, setErrors] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [confirmedPreview, setConfirmedPreview] = useState(null);
  const idempotencyKey = useRef(createPushIdempotencyKey());
  const createJob = useCreatePushNotification();
  const { uploadImage } = usePushImageActions();

  const currentDraft = useMemo(
    () => ({
      ...draft,
      audience: buildAudience(
        audienceType,
        selectedUsers,
        segmentCode,
        segmentDays,
      ),
    }),
    [draft, audienceType, selectedUsers, segmentCode, segmentDays],
  );
  const validationErrors = useMemo(
    () => validateAdvancedPushDraft(currentDraft),
    [currentDraft],
  );
  const previewRequest = useMemo(
    () => buildPushPreviewRequest(currentDraft),
    [currentDraft],
  );

  useEffect(() => {
    if (validationErrors.length) {
      setDebouncedRequest(null);
      return undefined;
    }
    const timeout = window.setTimeout(
      () => setDebouncedRequest(previewRequest),
      500,
    );
    return () => window.clearTimeout(timeout);
  }, [previewRequest, validationErrors.length]);

  const preview = usePushAudiencePreview(
    debouncedRequest,
    Boolean(debouncedRequest && capabilities?.readyToSend),
  );

  const setField = (field, value) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const togglePlatform = (platform) => {
    setDraft((current) => ({
      ...current,
      platforms: current.platforms.includes(platform)
        ? current.platforms.filter((value) => value !== platform)
        : [...current.platforms, platform],
    }));
  };

  const handleImage = (file) => {
    setErrors([]);
    if (!file) return;
    const allowedTypes = capabilities?.imageContentTypes ?? [
      "image/png",
      "image/jpeg",
    ];
    const maxBytes = capabilities?.maxImageBytes ?? 1_048_576;
    if (!allowedTypes.includes(file.type) || file.size > maxBytes) {
      setErrors(["Image must be a PNG or JPEG no larger than 1 MiB."]);
      return;
    }
    uploadImage.mutate(
      { file },
      {
        onSuccess: (asset) => setField("imageAsset", asset),
        onError: (error) => setErrors([error.message]),
      },
    );
  };

  const prepareConfirmation = async () => {
    setErrors(validationErrors);
    if (validationErrors.length || !capabilities?.readyToSend) return;
    try {
      const freshPreview = await preview.previewNow(previewRequest);
      setConfirmedPreview(freshPreview);
      setConfirming(true);
    } catch (error) {
      setErrors([error.message]);
    }
  };

  const submit = () => {
    if (createJob.isPending) return;
    createJob.mutate(
      {
        request: buildAdvancedPushRequest(currentDraft),
        idempotencyKey: idempotencyKey.current,
      },
      {
        onSuccess: (job) => {
          setConfirming(false);
          setDraft(initialDraft);
          setSelectedUsers(new Map());
          navigate(`/push-notifications/history/${job.id}`);
        },
        onError: (error) => {
          setConfirming(false);
          setErrors([
            `${error.message} You can submit this unchanged draft again; its idempotency key is retained.`,
          ]);
        },
      },
    );
  };

  const segmentOptions = capabilities?.supportedSegments?.length
    ? capabilities.supportedSegments
    : ["RECENTLY_JOINED", "INACTIVE"];
  const categoryOptions = capabilities?.categories?.length
    ? capabilities.categories
    : ["PROMOTION", "LOYALTY", "TRANSACTIONAL"];
  const platformOptions = capabilities?.platforms?.length
    ? capabilities.platforms
    : ["ANDROID", "IOS"];

  return (
    <>
      <PushFeedback errors={errors} />
      <div className="grid gap-6">
        <div className="grid gap-5 md:grid-cols-2">
          <label className="text-sm font-semibold">
            Title <span aria-hidden="true">*</span>
            <input
              aria-label="Title"
              maxLength="100"
              value={draft.title}
              onChange={(event) => setField("title", event.target.value)}
              className="mt-2 w-full rounded border border-slate-200 px-3 py-3 font-normal shadow-sm"
            />
          </label>
          <label className="text-sm font-semibold">
            Category <span aria-hidden="true">*</span>
            <select
              aria-label="Category"
              value={draft.category}
              onChange={(event) => setField("category", event.target.value)}
              className="mt-2 w-full rounded border border-slate-200 bg-white px-3 py-3 font-normal shadow-sm"
            >
              {categoryOptions.map((category) => (
                <option key={category}>{category}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="text-sm font-semibold">
          Message <span aria-hidden="true">*</span>
          <textarea
            aria-label="Message"
            rows="5"
            maxLength="1000"
            value={draft.message}
            onChange={(event) => setField("message", event.target.value)}
            className="mt-2 w-full rounded border border-slate-200 px-3 py-3 font-normal shadow-sm"
          />
          <span className="mt-1 block text-right text-xs font-normal text-slate-500">
            {draft.message.length}/1000
          </span>
        </label>
        <fieldset>
          <legend className="mb-2 text-sm font-semibold">Platforms</legend>
          <div className="flex flex-wrap gap-6">
            {platformOptions.map((platform) => (
              <label key={platform} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.platforms.includes(platform)}
                  onChange={() => togglePlatform(platform)}
                />
                {platform === "IOS" ? "iOS" : "Android"}
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <label className="text-sm font-semibold">
            Image <span className="font-normal">(optional)</span>
            <input
              aria-label="Image"
              type="file"
              accept="image/png,image/jpeg"
              disabled={uploadImage.isPending}
              onChange={(event) => handleImage(event.target.files?.[0])}
              className="mt-2 block w-full text-sm font-normal"
            />
          </label>
          {uploadImage.isPending ? (
            <p className="mt-2 text-sm">Uploading image…</p>
          ) : null}
          {draft.imageAsset ? (
            <div className="mt-3 flex items-center gap-4 rounded border border-slate-200 p-3">
              <img
                src={draft.imageAsset.url}
                alt="Notification preview"
                className="h-20 w-20 rounded object-cover"
              />
              <div className="text-sm">
                <div>Uploaded image #{draft.imageAsset.id}</div>
                <button
                  type="button"
                  onClick={() => setField("imageAsset", null)}
                  className="mt-2 text-red-700 underline"
                >
                  Remove from notification
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <fieldset>
          <legend className="mb-3 text-sm font-semibold">Audience</legend>
          <div className="mb-4 flex flex-wrap gap-6">
            {[
              ["ALL", "All users"],
              ["USERS", "Selected users"],
              ["SEGMENT", "Segment"],
            ].map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="audience"
                  checked={audienceType === value}
                  onChange={() => setAudienceType(value)}
                />
                {label}
              </label>
            ))}
          </div>
          {audienceType === "USERS" ? (
            <UserSelector
              selectedUsers={selectedUsers}
              onChange={setSelectedUsers}
            />
          ) : null}
          {audienceType === "SEGMENT" ? (
            <div className="grid gap-4 rounded border border-slate-200 bg-white p-4 sm:grid-cols-2">
              <label className="text-sm font-semibold">
                Segment
                <select
                  value={segmentCode}
                  onChange={(event) => setSegmentCode(event.target.value)}
                  className="mt-2 w-full rounded border border-slate-200 bg-white px-3 py-2 font-normal"
                >
                  {segmentOptions.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment === "RECENTLY_JOINED"
                        ? "Recently joined"
                        : "Inactive"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold">
                Days
                <input
                  aria-label="Segment days"
                  type="number"
                  min="1"
                  max="3650"
                  value={segmentDays}
                  onChange={(event) => setSegmentDays(event.target.value)}
                  className="mt-2 w-full rounded border border-slate-200 px-3 py-2 font-normal"
                />
              </label>
            </div>
          ) : null}
        </fieldset>
        <div className="rounded bg-slate-50 p-4">
          <h3 className="mb-4 font-semibold">Audience preview</h3>
          <AudiencePreview
            preview={preview.data}
            loading={preview.isFetching}
          />
          {preview.error ? (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {preview.error.message}
            </p>
          ) : null}
        </div>
        <div>
          <button
            type="button"
            disabled={
              !capabilities?.readyToSend ||
              createJob.isPending ||
              uploadImage.isPending
            }
            onClick={prepareConfirmation}
            className="rounded bg-primary hover:bg-primary-dark px-5 py-3 text-xs font-bold uppercase text-white disabled:bg-slate-400"
          >
            Review and send
          </button>
        </div>
      </div>
      <ConfirmDialog
        title="Confirm advanced notification"
        visible={confirming}
        onAccept={submit}
        onDismiss={() => setConfirming(false)}
      >
        <div className="space-y-5 text-sm">
          <div>
            <div className="font-semibold">{currentDraft.title}</div>
            <p className="mt-1 whitespace-pre-wrap">{currentDraft.message}</p>
          </div>
          <dl className="grid grid-cols-[10rem_1fr] gap-2">
            <dt>Category</dt>
            <dd>{currentDraft.category}</dd>
            <dt>Platforms</dt>
            <dd>{currentDraft.platforms.join(", ")}</dd>
            <dt>Audience</dt>
            <dd>{currentDraft.audience.type}</dd>
            <dt>Eligible installations</dt>
            <dd>{confirmedPreview?.eligibleInstallations ?? 0}</dd>
          </dl>
          <AudiencePreview preview={confirmedPreview} />
          <p className="font-medium text-amber-800">
            Sending creates a background job. Firebase acceptance is not proof
            of device delivery.
          </p>
        </div>
      </ConfirmDialog>
    </>
  );
}

export default AdvancedPushNotificationForm;
